import React, { useMemo } from 'react';
import { RNLLoader } from '../UI';

const NAVY = '#0b318b';
const GRID = '1px solid #cbd5e1';
const TD = { padding: '12px 9px', fontSize: 11.5, textAlign: 'center', borderRight: GRID, borderBottom: GRID };
const TH = {
  padding: '7px 9px', fontSize: 10, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase',
  whiteSpace: 'nowrap', textAlign: 'center', background: '#243b64', color: '#fff',
  borderRight: '1px solid rgba(255,255,255,0.28)', borderBottom: '1px solid #243b64',
};

// One visual identity per kind of event, so the trail can be scanned by
// colour before it is read.
const KIND = {
  posted:   { dot: '#64748b', label: 'Posted' },
  approved: { dot: '#16a34a', label: 'Approved for Payment' },
  returned: { dot: '#f97316', label: 'Returned' },
  rejected: { dot: '#ef4444', label: 'Rejected' },
  hold:     { dot: '#f59e0b', label: 'On Hold' },
  edited:   { dot: '#7c3aed', label: 'Corrected' },
  released: { dot: '#0f766e', label: 'Released' },
  pending:  { dot: '#cbd5e1', label: 'Awaiting' },
};

const when = (d) => {
  if (!d) return { date: '—', time: '' };
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return { date: String(d), time: '' };
  return {
    date: dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase(),
  };
};

/**
 * What actually happened to this voucher, in order.
 *
 * Assembled from three sources rather than one, because no single source has
 * the whole story: the workflow log holds the approval steps and who acted on
 * them, the voucher's own stamps hold posting and release (neither is a
 * workflow step), and the revision rows hold corrections made in between.
 * Merging them and sorting by time is what makes it a trail rather than three
 * separate lists.
 */
export default function VoucherApprovalHistory({ voucher, tracking, loading, error }) {
  const events = useMemo(() => {
    if (!voucher) return [];
    const out = [];

    if (voucher.posted_at || voucher.created_by_name) {
      out.push({
        kind: 'posted', at: voucher.posted_at,
        title: 'Voucher Posted', step: 'Submit',
        who: voucher.posted_by_name || voucher.created_by_name,
        note: 'Raised and submitted into the approval chain',
      });
    }

    (tracking?.approval_history || []).forEach((h) => {
      const kind = h.status === 'returned' ? 'returned'
        : h.status === 'rejected' ? 'rejected'
        : 'approved';
      out.push({
        kind, at: h.date_actioned,
        title: KIND[kind].label, step: h.step_name,
        who: h.approver_fullname,
        note: h.comments,
      });
    });

    // Corrections are grouped by version: one edit touching four fields is
    // one event in the trail, not four.
    const byVersion = {};
    (voucher.revisions || []).forEach((r) => {
      (byVersion[r.version] = byVersion[r.version] || []).push(r);
    });
    Object.entries(byVersion).forEach(([version, rows]) => {
      out.push({
        kind: 'edited', at: rows[0].changed_at,
        title: 'Corrected', step: '—', version: Number(version),
        who: rows[0].changed_by_name,
        note: rows[0].reason,
        changes: rows,
      });
    });

    if (voucher.review_on_hold && voucher.review_hold_at) {
      out.push({
        kind: 'hold', at: voucher.review_hold_at,
        title: 'Placed on Hold', step: '—',
        who: voucher.review_hold_by_name,
        note: voucher.review_hold_reason,
      });
    }

    if (voucher.released_to_expense_register) {
      out.push({
        kind: 'released', at: voucher.released_at,
        title: 'Released to Expense Register', step: 'Expense Register',
        who: voucher.released_by_name,
        note: 'Payment settled and posted to the register',
      });
    }

    // Undated events sort last rather than jumping to 1970.
    return out.sort((a, b) => {
      if (!a.at && !b.at) return 0;
      if (!a.at) return 1;
      if (!b.at) return -1;
      return new Date(a.at) - new Date(b.at);
    });
  }, [voucher, tracking]);

  // Only the trailing "still with X" marker is derived now — the status and
  // version walk was removed along with the columns it fed.
  const rows = useMemo(() => {
    const current = tracking?.current_step;
    if (!current || voucher?.released_to_expense_register) return events;
    return [...events, {
      kind: 'pending', at: null,
      title: 'Awaiting', step: current.step_name,
      who: current.assigned_to?.fullname || current.assigned_to?.email || null,
      note: 'Not yet actioned',
    }];
  }, [events, tracking, voucher]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><RNLLoader title="Loading history…" /></div>;
  if (error) return <div style={{ padding: 20, fontSize: 12, color: 'var(--red)' }}>{error}</div>;
  if (!voucher) return null;

  const last = rows.length - 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-secondary)' }}>
        <span>Voucher <strong style={{ color: NAVY, fontFamily: 'var(--mono)' }}>{voucher.voucher_no}</strong></span>
        <span>Version <strong>v{voucher.version || 1}</strong></span>
        <span>{rows.length} event{rows.length === 1 ? '' : 's'}</span>
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Nothing has happened to this voucher yet.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* border-collapse: separate so the rail cell can paint its own
              connector — collapsed borders belong to the table, not the cell,
              and would clip it. */}
          <table style={{
            width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 780,
            borderTop: GRID, borderLeft: GRID,
          }}>
            <thead>
              <tr>
                <th style={{ ...TH, width: 34, minWidth: 34 }} />
                {['Date', 'Time', 'User', 'Step', 'Action', 'Comment']
                  .map(h => <th key={h} style={TH}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((e, i) => {
                const meta = KIND[e.kind] || KIND.posted;
                const t = when(e.at);
                const pending = e.kind === 'pending';
                return (
                  <tr key={i} style={pending ? { background: '#f8fafc' } : undefined}>
                    {/* The rail: connector behind, dot on top. Clipped at the
                        first and last rows so the line starts and ends on a
                        dot rather than floating past them. */}
                    {/* Row rules stop at the rail so the connector reads as one
                        unbroken line — except on the final row, which keeps its
                        border so the table's bottom edge closes. */}
                    <td style={{
                      ...TD, borderBottom: i === last ? GRID : 'none',
                      position: 'relative', padding: 0, width: 34, minWidth: 34,
                    }}>
                      <div style={{
                        position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                        top: i === 0 ? '50%' : 0,
                        bottom: i === last ? '50%' : 0,
                        width: 2, background: '#e2e8f0',
                      }} />
                      <div style={{
                        position: 'relative', margin: '0 auto',
                        width: 13, height: 13, borderRadius: 999,
                        background: pending ? '#fff' : meta.dot,
                        border: pending ? `2px dashed ${meta.dot}` : '2px solid #fff',
                        boxShadow: `0 0 0 1px ${pending ? '#cbd5e1' : meta.dot}`,
                      }} />
                    </td>
                    <td style={{ ...TD, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{t.date}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>{t.time || '—'}</td>
                    <td style={{ ...TD, fontWeight: 700 }}>
                      {e.who || <em style={{ color: '#94a3b8', fontWeight: 400 }}>not recorded</em>}
                    </td>
                    <td style={{ ...TD, color: '#475569', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.3, fontSize: 10.5 }}>{e.step || '—'}</td>
                    <td style={{ ...TD, width: 132, minWidth: 132, maxWidth: 132 }}>
                      {/* The pill wraps rather than forcing the column wide —
                          "Released to Expense Register" on one line was taking
                          space the comment needed. */}
                      <span style={{
                        display: 'inline-block',
                        background: `${meta.dot}18`, color: meta.dot, border: `1px solid ${meta.dot}55`,
                        padding: '3px 9px', borderRadius: 11, fontSize: 10, fontWeight: 800,
                        textTransform: 'uppercase', letterSpacing: 0.3,
                        whiteSpace: 'normal', lineHeight: 1.35,
                      }}>{e.title}</span>
                    </td>
                    <td style={{
                      ...TD, textAlign: 'left', color: '#64748b',
                      // Fixed width + break-word so a long comment wraps inside
                      // the cell instead of stretching the table.
                      whiteSpace: 'normal', width: 330, minWidth: 330, maxWidth: 330,
                      overflowWrap: 'anywhere',
                    }}>
                      {e.note ? <span style={{ fontStyle: 'italic' }}>&ldquo;{e.note}&rdquo;</span> : '—'}
                      {e.changes && (
                        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {e.changes.map((c, j) => (
                            <div key={j} style={{ fontSize: 10.5 }}>
                              <strong style={{ color: '#475569' }}>{c.field_label}:</strong>{' '}
                              <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>{c.original_value ?? '—'}</span>
                              {' \u2192 '}
                              <strong style={{ color: '#0f172a' }}>{c.revised_value ?? '—'}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
